// list of available IANA time zones using native Intl API
export const getAllTimezones = (): string[] => {
    let zones: string[]
    if (
        typeof Intl !== 'undefined' &&
        typeof (Intl as any).supportedValuesOf === 'function'
    ) {
        zones = (Intl as any).supportedValuesOf('timeZone')
    } else {
        zones = [
            'America/Los_Angeles',
            'America/New_York',
            'Europe/London',
            'Asia/Kolkata',
            'Asia/Tokyo',
            'Africa/Accra',
            'Australia/Sydney',
            'Pacific/Fiji',
        ]
    }
    // Intl.supportedValuesOf('timeZone') does not include "UTC" — add it
    // so public-API created shipments (which always store timezone as "UTC")
    // can be correctly displayed and selected in the timezone dropdown.
    if (!zones.includes('UTC')) {
        zones = ['UTC', ...zones]
    }
    return zones
}

// gmt offset string format
export function getGmtOffsetString(timeZone: string): {
    label: string
    offsetMinutes: number
} {
    const now = new Date()
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
    const parts = dtf.formatToParts(now)
    const get = (type: string) =>
        Number(parts.find((p) => p.type === type)?.value)

    const asUTC = Date.UTC(
        get('year'),
        get('month') - 1,
        get('day'),
        get('hour'),
        get('minute'),
        get('second'),
    )
    const offsetMinutes = (asUTC - now.getTime()) / 60000
    const sign = offsetMinutes <= -1 ? '-' : '+'
    const absMinutes = Math.abs(offsetMinutes)
    const totalMinutes = Math.round(absMinutes)
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
    const minutes = String(totalMinutes % 60).padStart(2, '0')

    return {
        label: `GMT${sign}${hours}:${minutes}`,
        offsetMinutes,
    }
}

// Module-level cache — timezone offsets are stable for the lifetime of the session.
let _allTimezoneOptionsCache:
    | {
          label: string
          value: string
          offsetMinutes: number
      }[]
    | null = null

export function getAllTimezoneOptions(): {
    label: string
    value: string
    offsetMinutes: number
}[] {
    if (_allTimezoneOptionsCache) return _allTimezoneOptionsCache
    const allTimezones = getAllTimezones()
    _allTimezoneOptionsCache = allTimezones.map((tz) => {
        const { label, offsetMinutes } = getGmtOffsetString(tz)
        return {
            label: `(${label}) ${tz}`,
            value: tz,
            offsetMinutes,
        }
    })
    return _allTimezoneOptionsCache
}

// cache for timezone labels to avoid repeated lookups
const timezoneLabelCache: Record<string, string> = {}

export function getSelectedTimezoneLabel(preferredTimeZone: string): string {
    if (timezoneLabelCache[preferredTimeZone]) {
        return timezoneLabelCache[preferredTimeZone]
    }
    const label =
        getAllTimezoneOptions().find((opt) => opt.value === preferredTimeZone)
            ?.label || preferredTimeZone
    timezoneLabelCache[preferredTimeZone] = label
    return label
}
